import { queryClient } from "./queryClient";
import {
  assessUpdateSafety,
  type UpdateSafetySignals,
  type UpdateSafetyVerdict,
} from "../../shared/update-safety";

/**
 * 새 버전 자동 업데이트 — 브라우저에서 "지금 작업 중인가" 신호를 모으는 자리.
 * 판정 규칙은 shared/update-safety.ts(순수 함수·테스트). 여기는 DOM·네트워크만 본다.
 *
 * 모으는 신호:
 *  - 마지막 클릭·타이핑 시각(installActivityTracking)
 *  - 진행 중인 저장·업로드 요청 수 — window.fetch 를 한 겹 감싸 GET 이 아닌 요청을 센다.
 *    서버 호출이 40여 파일에 흩어져 있어 한 곳에서 세려면 이 길뿐이다. react-query mutation 도 더한다.
 *  - beforeunload 를 막는 화면인가 — 가짜 beforeunload 를 한 번 쏴서 preventDefault 됐는지 본다.
 *    화면마다 따로 등록하지 않아도, "떠나면 잃는다"고 이미 선언한 화면(시세표 편집기·대리점 정보
 *    수정·통화 녹음)이 전부 잡힌다. 앞으로 그런 화면을 만들 때도 beforeunload 만 걸면 된다.
 *  - 열린 팝업 수 — 공용 Modal·ModalShell 오버레이가 공통으로 다는 `.modal` 클래스를 센다.
 *    새 버전 모달 자신은 안 세도 된다 — 자동으로 넘길지 판단하는 시점(처음 알아챈 순간·
 *    「나중에 하기」 뒤)에는 그 모달이 닫혀 있다.
 *  - 입력칸 포커스.
 */

let lastActivityAt = 0;
let inFlightWrites = 0;
let installed = false;

/** 조작·요청 추적을 켠다. 한 번만 설치되며(중복 호출 무시) 해제하지 않는다 — 앱 수명과 같다. */
export function installActivityTracking(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const touch = () => {
    lastActivityAt = Date.now();
  };
  for (const ev of ["pointerdown", "keydown", "input", "touchstart"] as const) {
    window.addEventListener(ev, touch, { capture: true, passive: true });
  }

  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const method = (
      init?.method ??
      (typeof input !== "string" && !(input instanceof URL) ? input.method : "GET")
    ).toUpperCase();
    const isWrite = method !== "GET" && method !== "HEAD";
    if (isWrite) inFlightWrites += 1;
    try {
      return await original(input, init);
    } finally {
      if (isWrite) inFlightWrites -= 1;
    }
  };
}

function isUnloadGuarded(): boolean {
  try {
    const probe = new Event("beforeunload", { cancelable: true });
    window.dispatchEvent(probe);
    return probe.defaultPrevented;
  } catch {
    return false;
  }
}

function countOpenModals(): number {
  return document.querySelectorAll(".modal").length;
}

function isEditing(): boolean {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) {
    // 체크박스·버튼류는 "치는 중"이 아니다. 나머지(text·number·file·date …)는 입력 중으로 본다.
    return !["checkbox", "radio", "button", "submit", "reset"].includes(el.type);
  }
  return el instanceof HTMLElement && el.isContentEditable;
}

export function collectUpdateSafetySignals(pathname: string): UpdateSafetySignals {
  return {
    pathname,
    unloadGuarded: isUnloadGuarded(),
    openModals: countOpenModals(),
    editing: isEditing(),
    inFlightWrites: inFlightWrites + queryClient.isMutating(),
    msSinceActivity: lastActivityAt ? Date.now() - lastActivityAt : Infinity,
  };
}

/** 지금 새로고침해도 되나. */
export function checkUpdateSafety(pathname: string): UpdateSafetyVerdict {
  return assessUpdateSafety(collectUpdateSafetySignals(pathname));
}
